import {useCallback, useEffect, useMemo, useState} from 'react'
import apiClient from '../../../../services/api-client'
import {useReduxAuth} from '../../../auth'
import {toast} from 'react-toastify'
import DataTableList from '../../components/DataTableList'
import {NURSE_HEADERS} from '../../../../constants/RoleConstants'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import Loader from '../../components/Loader'
import CreateUserFrom from '../../components/forms/StatePersistedCreateUserForm'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {filterHeadersByUserRole} from '../../helper/helperFunction'
import {RoutesMetaForDoctors} from '../../modules/accounts/components/CRUDWrapper/routesMeta'
import MetaTags from '../../../../meta/MetaTags'
import {Modal} from 'react-bootstrap'
import {FaTimesCircle} from 'react-icons/fa'
import PrimaryButton from '../../components/common/buttons/PrimaryButton'
import ConfirmationModal from '../../components/common/modals/ConfirmationModal'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'
import axios from 'axios'

// Stable noop function to prevent useEffect dependency changes
const noop = () => {}

const NurseListingPage = () => {
  // toast alerts
  const notifyError = useCallback((msg: string) => {
    toast.error(msg)
  }, [])

  const notifySuccess = useCallback((msg: string) => {
    toast.success(msg)
  }, [])

  const [userData, setUserData] = useState<any>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isInviteLoading, setIsInviteLoading] = useState(false)
  const {currentUser} = useReduxAuth()
  
  // MA doctor dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  const setMaSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.setSelectedDoctorId : noop
  
  // State for doctor dropdown (for MA)
  const [maDoctors, setMaDoctors] = useState<Array<{id: number; full_name: string}>>([])

  // Form dirty state tracking
  const [formDirty, setFormDirty] = useState(false)
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [resetForm, setResetForm] = useState(false)
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false)
  const [isStausConfirmationModalOpen, setIsStausConfirmationModalOpen] = useState(false)
  const [selectedStaff , setSelectedStaff] = useState<any>(null)


  // Fetch all doctors for MA (MA can access all doctors via dropdown)
  useEffect(() => {
    if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT) {
      axios
        .get(API_ENDPOINTS.GET_ALL_DOCTORS())
        .then((resp) => {
          const fetchedDoctors = resp.data.data?.doctors || resp.data.data || []
          setMaDoctors(fetchedDoctors)
          if (fetchedDoctors.length > 0 && !maSelectedDoctorId) {
            setMaSelectedDoctorId(fetchedDoctors[0].id)
          }
        })
        .catch((err) => {
          console.error('Error fetching doctors for MA:', err)
          toast.error('Failed to load doctors')
          setMaDoctors([])
        })
    }
  }, [currentUser?.role_id, maSelectedDoctorId, setMaSelectedDoctorId])

  // function to get data
  const fetchData = useCallback(async () => {
    // Skip if MA and no doctor selected
    if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && !maSelectedDoctorId) {
      setUserData([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    let response

    try {
      if (
        currentUser?.role_id === USER_ROLES.ADMIN ||
        currentUser?.role_id === USER_ROLES.SUB_ADMIN
      ) {
        response = await apiClient.get(API_ENDPOINTS.GET_ALL_NURSES()) // 🔹 Fetch all nurses (Admin)
      } else if (currentUser?.role_id === USER_ROLES.DOCTOR) {
        // 🔹 Use the new GET_DOCTOR_STAFF endpoint for doctors to get both nurses and billing personnel
        response = await apiClient.get(API_ENDPOINTS.GET_DOCTOR_STAFF(currentUser?.id))
      } else if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
        response = await apiClient.get(API_ENDPOINTS.GET_DOCTOR_STAFF(maSelectedDoctorId))
      }

      // Handle different response structures
      const items = response?.data?.staff ?? response?.data?.data?.nurses ?? []
      setUserData(items)
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
      setUserData([])
    } finally {
      setIsLoading(false)
    }
  }, [currentUser, maSelectedDoctorId, notifyError]) 

  // fetch data
  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData])

  // create new user(nurse)
  const handleSubmit = async (values: any, resetForm: () => void) => {
    setIsLoading(true)
    setIsInviteLoading(true)

    try {
      // For MA, use selected doctor ID; for doctors, use their own ID
      const doctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? maSelectedDoctorId
        : currentUser?.id

      if(values?.category == 1){
        const response = await apiClient.post(API_ENDPOINTS.INVITE_USER('nurse', doctorId), values)
        const {
          message,
          data: {newUser},
        } = response.data
        notifySuccess(message)

        // ← append the full object exactly as the table expects
        setUserData((prev: any[]) => [...prev, newUser])
  
      }
      else{
        // invite billing personnel
        const requestBody: any = {
          email: values.email,
          name: values.name,
        }
        if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
          requestBody.doctor_id = maSelectedDoctorId
        }
        
        const response = await apiClient.post(API_ENDPOINTS.SEND_INVITE_TO_BILLING_PERSONNEL(), requestBody)
        notifySuccess(response.data.message || 'Invitation sent successfully!')

        const { data } = response.data;

        setUserData((prev: any[]) => [
          ...prev,
          {
            id: data?.id,
            full_name: data?.name,  
            email: data?.email,
            status: data?.status,
            role_id: USER_ROLES.BILLING_PERSONNEL,
          },
        ])
  
      }


      resetForm() // clear the form
      setIsModalOpen(false) // close the modal
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    } finally {
      setIsInviteLoading(false)
      setIsModalOpen(false)
      setIsLoading(false)
    }
  }

  // handle update status
  const handleUpdateStatus = async (row: any) => {
    if (!row) return
    // For billing personnel, handle access revocation/granting via junction table
    // For nurses, toggle their user status as before
    if (row.role_id === USER_ROLES.BILLING_PERSONNEL) {
      const hasAccess = row.has_access === 1 || row.has_access === true

      if (hasAccess) {
        // Revoke access

        setIsLoading(true)
        const originalList = [...userData]

        // Optimistically update UI to show revoked access
        setUserData((prev: any[]) =>
          prev.map((staff) => (staff.id === row.id ? {...staff, has_access: false} : staff))
        )

        try {
          await apiClient.delete(API_ENDPOINTS.UNLINK_BILLING_PERSONNEL(row.id))
          notifySuccess('Billing personnel access revoked successfully')
        } catch (error: any) {
          // Restore original list on error
          setUserData(originalList)
          notifyError(error?.response?.data?.message ?? 'Failed to revoke access')
        } finally {
          setIsLoading(false)
          setIsStausConfirmationModalOpen(false)
        }
      } else {

        setIsLoading(true)
        const originalList = [...userData]

        // Optimistically update UI to show granted access
        setUserData((prev: any[]) =>
          prev.map((staff) => (staff.id === row.id ? {...staff, has_access: true} : staff))
        )

        try {
          await apiClient.post(API_ENDPOINTS.GRANT_BILLING_PERSONNEL_ACCESS(row.id))
          notifySuccess('Billing personnel access granted successfully')
        } catch (error: any) {
          // Restore original list on error
          setUserData(originalList)
          notifyError(error?.response?.data?.message ?? 'Failed to grant access')
        } finally {
          setIsLoading(false)
          setIsStausConfirmationModalOpen(false)
        }
      }
    } else {
      // For nurses, toggle user status
      const newStatus = row.status === 1 ? 0 : 1

      try {
        const response = await apiClient.put(API_ENDPOINTS.UPDATE_USER_STATUS(row.id, newStatus))

        notifySuccess(response?.data?.message)

        // Update only the nurse whose ID matches
        const updatedUserData = userData.map((nurse: any) => {
          if (nurse?.id === row?.id) {
            return {
              ...nurse,
              status: newStatus,
            }
          }
          return nurse
        })

        setUserData(updatedUserData)
      } catch (error: any) {
        notifyError(error?.response?.data?.message)
      } finally {
        setIsLoading(false)
        setIsStausConfirmationModalOpen(false)
        setSelectedStaff(null) // Clear selected staff after action
      }
    }
  }

  const handleDeleteStaff = async (row: any) => {
    if (!row) return

    setIsLoading(true)
    const originalList = [...userData]
    setUserData((prev: any[]) => prev.filter((staff) => staff.id !== row.id))

    try {
      const response = await apiClient.put(API_ENDPOINTS.DELETE_STAFF(row.id))
      notifySuccess(response?.data?.message)
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
      setUserData(originalList)
    } finally {
      setIsLoading(false)
      setIsDeleteConfirmationModalOpen(false);
    }
  }

  const handleResendClick = async (row: any) => {
    if (!row) return

    try {
      const response = await apiClient.put(API_ENDPOINTS.RESEND_EMAIL_TO_STAFF(row.id))
      notifySuccess(response?.data?.message)
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    }
  }

  // row actions
  const handleActionPerformed = async (action: any, row: any) => {
    if (action.title === 'Resend Email') {
      handleResendClick(row)
    }
    if (action.title === 'Delete') {
      setIsDeleteConfirmationModalOpen(true);
      setSelectedStaff(row);
    }
    if (action.name === 'CANCEL_INVITE') {
    try {
      await apiClient.patch(API_ENDPOINTS.CANCEL_BILLER_INVITE(row.id))
      notifySuccess('Invite cancelled successfully!')
      // refresh data
      fetchData()
    } catch (error: any) {
      notifyError(error?.response?.data?.message || 'Failed to cancel invite')
    }
  }
  } // Function to handle modal close with unsaved changes check
  const handleModalClose = () => {
    if (formDirty) {
      setShowDiscardModal(true)
      // The form will be hidden by CSS when showDiscardModal is true
    } else {
      setIsModalOpen(false)
    }
  }
  const [isActiveStatus ,setIsActiveStatus] = useState(true);
  const handleStatusToggle = (row: any) => {
    const isBillingPersonnel = row.role_id === USER_ROLES.BILLING_PERSONNEL
    if (
      isBillingPersonnel &&
      (currentUser?.role_id === USER_ROLES.ADMIN)
    ) {
      return
    }
    // if is billing personnel
      const isActive = isBillingPersonnel ? Boolean(row.has_access) : row.status === 1
      setIsActiveStatus(isActive);
    setSelectedStaff(row)
    setIsStausConfirmationModalOpen(true)
  }
  // Function to discard changes and close the form
  const discardChanges = () => {
    setFormDirty(false)
    setShowDiscardModal(false)
    setResetForm(true) // Trigger form reset
    setIsModalOpen(false)

    // Reset the resetForm flag after a small delay
    setTimeout(() => {
      setResetForm(false)
    }, 100)
  }

  // Function to keep editing and return to the form
  const keepEditing = () => {
    setShowDiscardModal(false)
    // The form will be shown again by CSS when showDiscardModal is false
  }

  // Function to handle form submission and reset dirty state
  const handleFormSubmit = (values: any, resetForm: () => void) => {
    handleSubmit(values, resetForm)
    setFormDirty(false)
  }

  // Function to filter headers based on user permissions
  const filteredHeaders = useMemo(() => {
    return filterHeadersByUserRole(NURSE_HEADERS, currentUser)
  }, [currentUser])
  const canInvite = currentUser?.role_id === USER_ROLES.DOCTOR || currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT

  // search filters from datatables
  const [searchText, setSearchText] = useState('')
  const [staffCategory , setStaffCategory] = useState('all')
  const filteredData = useMemo(() => {
    return userData.filter((doctor: any) => {
      // Search text filter
      const searchFilter =
        searchText === '' ||
        (doctor.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          doctor.email?.toLowerCase().includes(searchText.toLowerCase()))
          const isBillingPersonnel = doctor.role_id === USER_ROLES.BILLING_PERSONNEL

          // const manualSubFilterMatch =
          // staffCategory == 'all' ||
          // (staffCategory == 'true' && isActive) ||
          // (staffCategory == 'false' && !isActive)

          const manualSubFilterMatch =
          staffCategory === 'all' ||
          (staffCategory === 'staff' && !isBillingPersonnel) ||
          (staffCategory === 'biller' && isBillingPersonnel)

      return searchFilter && manualSubFilterMatch
    })
  }, [userData, searchText , staffCategory])
  const handleFilterChange = (key: string, value: string) => {
    if (key === 'search') {
      setSearchText(value)
    }
    if (key === 'has_access'){
      setStaffCategory(value)
    }
  }
  const handleClearAllFilters = () => {
    setSearchText('')
  }

  const customFilters = [
    {
      name: 'Search',
      key: 'search',
      type: 'search' as const,
      placeholder: 'Search by name or email',
      value: searchText,
    },
    // {
    //   name: 'Staff status',
    //   key: 'status',
    //   type: 'select' as const,
    //   value: staffCategory,
    //   options: [
    //     {value: 'all', label: 'All'},
    //     {value: 'true', label: 'Active'},
    //     {value: 'false', label: 'Inactive'},
    //   ],
    // },
    {
      name: 'Staff role',
      key: 'has_access',
      type: 'select' as const,
      value: staffCategory,
      options: [
        {value: 'all', label: 'All'},
        {value: 'staff', label: 'Staff'},
        {value: 'biller', label: 'Biller'},
      ],
    },
  ]
  const staffTypes = [
    {id: 1, name: 'Staff'},
    {id: 2, name: 'Biller'},
  ]

  return (
    <>
      <MetaTags
        title={currentUser?.role_id === USER_ROLES.DOCTOR ? 'Staff Management' : 'Nurse List'}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {/* Doctor Dropdown for MA */}
          {currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && (
            <div className='d-flex justify-content-end mb-3'>
              <div className='w-25'>
                <label className='form-label'>Select Doctor</label>
                <select
                  className='form-select'
                  value={maSelectedDoctorId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    setMaSelectedDoctorId(val ? Number(val) : undefined)
                  }}
                  disabled={maDoctors.length === 0}
                >
                {maDoctors.length === 0 ? (
                  <option value=''>-- No doctors available --</option>
                ) : (
                  <>
                    <option value=''>-- Select Doctor --</option>
                    {maDoctors.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.full_name}
                      </option>
                    ))}
                  </>
                )}
                </select>
              </div>
            </div>
          )}

          <DataTableList
            title='Staff' 
            columns={filteredHeaders} // uncomment to enable permissions
            // columns={NURSE_HEADERS}
            data={filteredData}
            showAddButton={canInvite}
            onAddClick={() => setIsModalOpen(!isModalOpen)}
            addButtonText='Add Staff'
            actions={RoutesMetaForDoctors.nurse.actions}
            onAction={handleActionPerformed}
            isLoading={isLoading}
            handleStatusToggle={handleStatusToggle}
            showOnlySearch={true}
            showCustomFilters={true}
            customFilters={customFilters}
            onCustomFilterChange={handleFilterChange}
            onClearAllCustomFilters={handleClearAllFilters}
            allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.MEDICAL_ASSISTANT]}
          />

          {/* create user form */}
          <div className={showDiscardModal ? 'd-none' : ''}>
            <CreateUserFrom
              title='Invite Staff'
              isInvitingStaff={true}
              staffTypes={staffTypes}
              isOpen={isModalOpen}
              onClose={handleModalClose}
              isLoading={isInviteLoading}
              handleSubmit={handleFormSubmit}
              setFormDirty={setFormDirty}
              shouldReset={resetForm}
            />
          </div>

          <ConfirmationModal
            isOpen={isDeleteConfirmationModalOpen}
            onClose={() => {
              setIsDeleteConfirmationModalOpen(false)
              setSelectedStaff(null)
            }}
            onConfirm={() => handleDeleteStaff(selectedStaff)}
            title='Delete Staff'
            message={`Are you sure you want to delete the staff member ${selectedStaff?.full_name}?`}
            confirmButtonText="Delete"
            isLoading={isLoading}
          />

          <ConfirmationModal
            isOpen={isStausConfirmationModalOpen}
            onClose={() => {
              setIsStausConfirmationModalOpen(false)
              setSelectedStaff(null)
            }}
            onConfirm={() => handleUpdateStatus(selectedStaff)}
            title={`${isActiveStatus ? 'Deactivate' : 'Activate'} Staff`}
            message={`Are you sure you want to ${isActiveStatus ? 'deactivate' : 'activate'} status for staff member ${selectedStaff?.full_name}?`}
            confirmButtonText={`${isActiveStatus ? 'Deactivate' : 'Activate'}`}
            isLoading={isLoading}
          />
        </>
      )}

      {/* Discard changes modal */}
      <Modal show={showDiscardModal} onHide={keepEditing} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTimesCircle className='text-danger me-2' />
            Unsaved Changes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have entered information that hasn't been saved yet. Are you sure you want to discard
          your changes?
        </Modal.Body>
        <Modal.Footer>
          <PrimaryButton color='secondary' onClick={keepEditing}>
            Keep Editing
          </PrimaryButton>
          <PrimaryButton color='danger' onClick={discardChanges}>
            Discard Changes
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default NurseListingPage
